import { useRef, type FC } from 'react';

interface DateFilterProps {
  onDateChange: (startDate: string | null, endDate: string | null) => void;
  startDate: string | null;
  endDate: string | null;
  onClearFilters?: () => void;
  tagInput: string;
  onTagInputChange: (value: string) => void;
  onTagInputSubmit: () => void;
  selectedTags: string[];
  availableTags: string[];
  onAddTag: (tag: string) => void;
  onRemoveTag: (tag: string) => void;
  isSearching?: boolean;
  searchResultCount?: number | null;
}

function pluralizeEvents(count: number): string {
  if (count === 1) return 'wydarzenie';
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'wydarzenia';
  return 'wydarzeń';
}

function formatDatePL(value: string | null): string {
  if (!value) return 'Wybierz datę';
  const [year, month, day] = value.split('-');
  return `${day}.${month}.${year}`;
}

interface DatePickerButtonProps {
  id: string;
  label: string;
  value: string | null;
  onChange: (value: string) => void;
  min?: string;
}

const DatePickerButton: FC<DatePickerButtonProps> = ({ id, label, value, onChange, min }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    const input = inputRef.current;
    if (!input) return;
    try {
      input.showPicker();
    } catch {
      input.focus();
    }
  };

  return (
    <div className="w-full">
      <label htmlFor={id} className="block cn-menu-title mb-1">
        {label}
      </label>
      <div className="relative w-full">
        <button
          type="button"
          onClick={openPicker}
          aria-label={label}
          className="w-full glass-input text-left relative flex items-center cursor-pointer select-none"
          style={{ paddingLeft: '12px', paddingRight: '12px', fontSize: '12px' }}
        >
          <span className="w-full text-center" style={{ color: value ? '#0b0136' : '#312e81' }}>
            {formatDatePL(value)}
          </span>
        </button>
        <input
          ref={inputRef}
          type="date"
          id={id}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          tabIndex={-1}
          aria-hidden="true"
          className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
          style={{ zIndex: -1 }}
        />
      </div>
    </div>
  );
};

export const DateFilter: FC<DateFilterProps> = ({
  onDateChange,
  startDate,
  endDate,
  onClearFilters,
  tagInput,
  onTagInputChange,
  selectedTags,
  availableTags,
  isSearching,
  searchResultCount,
}) => {
  const today = new Date().toISOString().split('T')[0];

  const handleClear = () => {
    onDateChange(null, null);
    onClearFilters?.();
  };

  return (
    <>
      <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-end lg:gap-2">
        {/* Date pickers */}
        <div id="date-filter-container" className="grid grid-cols-2 gap-2 w-full lg:w-auto lg:shrink-0 lg:mt-0">
          <div id="start-date-container" className="w-full" style={{ minWidth: '110px' }}>
            <DatePickerButton
              id="start-date"
              label="Od dnia:"
              value={startDate}
              onChange={(v) => {
                if (endDate && v > endDate) {
                  const next = new Date(v);
                  next.setDate(next.getDate() + 1);
                  onDateChange(v, next.toISOString().split('T')[0]);
                } else {
                  onDateChange(v, endDate);
                }
              }}
              min={today}
            />
          </div>
          <div id="end-date-container" className="w-full" style={{ minWidth: '110px' }}>
            <DatePickerButton
              id="end-date"
              label="Do dnia:"
              value={endDate}
              onChange={(v) => onDateChange(startDate, v)}
              min={startDate || today}
            />
          </div>
        </div>

        {/* Search input + tag chips, grouped so tags render directly under the search box */}
        <div id="search-column" className="flex flex-col w-full lg:w-auto lg:flex-1 lg:min-w-48">
          <span className="hidden lg:block cn-menu-title mb-1 invisible" aria-hidden="true">
            Szukaj
          </span>
          <div id="phrase-input" className="flex lg:mt-0 lg:mb-0">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => onTagInputChange(e.target.value)}
              placeholder="Szukaj po frazie lub tagu..."
              className="flex-1 glass-input text-center placeholder:text-center"
              style={{ fontSize: '12px' }}
            />
          </div>

          <div id="available-tags" className="flex flex-wrap gap-2 mt-2">
            {availableTags
              .filter((tag) => !selectedTags.includes(tag))
              .slice(0, 10)
              .map((tag, index) => (
                <button
                  key={tag}
                  onClick={() => onTagInputChange(tag)}
                  className={`inline-flex items-center rounded-full ${index >= 3 ? 'hidden lg:inline-flex' : ''}`}
                  style={{
                    backgroundColor: '#000000',
                    color: '#ffffff',
                    paddingTop: '6px',
                    paddingBottom: '6px',
                    paddingLeft: '10px',
                    paddingRight: '10px',
                    fontSize: '13px',
                    fontWeight: 600,
                    lineHeight: '1',
                  }}
                >
                  {tag}
                </button>
              ))}
          </div>
        </div>

        {/* Clear + add-event buttons – stay in the first row on desktop */}
        <div id="filter-buttons" className="flex flex-col w-full lg:w-auto lg:shrink-0">
          <span className="hidden lg:block cn-menu-title mb-1 invisible" aria-hidden="true">
            Akcje
          </span>
          <div className="flex flex-col gap-2 lg:flex-row">
            <button
              id="clear-filter-button"
              onClick={handleClear}
              className="w-full md:w-auto px-4 glass-button"
              style={{ fontSize: '12px' }}
            >
              Wyczyść filtry
            </button>
            <button
              id="add-event-filter-button"
              onClick={() => window.dispatchEvent(new CustomEvent('openAddEventModal'))}
              className="w-full md:w-auto px-4 glass-button"
              style={{ background: 'rgba(57, 255, 20, 0.15)', borderColor: 'rgba(57, 255, 20, 0.7)', color: '#39ff14', textShadow: '0 0 8px rgba(57, 255, 20, 0.8)', fontSize: '12px' }}
            >
              Zgłoś wydarzenie
            </button>
          </div>
        </div>

        {/* Search status – wraps to full-width row below on desktop */}
        {tagInput.length >= 3 && (
          <div id="search-status" className="text-sm text-center -mt-1 font-medium md:w-full" style={{ color: '#ffffff' }}>
            {isSearching ? (
              <span className="text-slate-300 italic">Szukam…</span>
            ) : searchResultCount !== null && searchResultCount !== undefined ? (
              <span>
                Znaleziono:{' '}
                <span className="font-bold">{searchResultCount}</span>{' '}
                {pluralizeEvents(searchResultCount)}
              </span>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
};
